import { useState, useEffect } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { useAuth } from '../../hooks/useAuth.js';
import api from '../../services/api.js';
import { formatarCNPJ, formatarCPF, formatarTelefone } from '../../utils/formatadores.js';
import ChatComponent from '../../components/ChatComponent';
import Preloader from '../../components/Preloader';

export default function Cockpit() {
  const { user } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);

  useEffect(() => {
    const fetchEmpresa = async () => {
      const idEmpresa = user?.id_empresa || user?.idempresa;
      if (idEmpresa) {
        setLoadingEmpresa(true);
        try {
          const res = await api.get(`/empresa/${idEmpresa}`);
          setEmpresa(res.data.dados || res.data);
        } catch (error) {
          console.error("Erro ao buscar empresa:", error);
        } finally {
          setLoadingEmpresa(false);
        }
      }
    };

    fetchEmpresa();
  }, [user]);

  return (
    <Box sx={{
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'calc(100vh - 70px)',
      position: 'relative', // Necessário para o logo absoluto
      overflow: 'hidden' // Evitar scroll se o logo for muito grande
    }}>
      {/* Watermark Logo Centralizado */}
      {empresa?.st_logotipo && (
        <Box
          component="img"
          src={empresa.st_logotipo}
          alt="Logo Watermark"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '50%', // Tamanho Grande
            maxWidth: 600,
            opacity: 0.05, // Bem transparente
            zIndex: 0,
            pointerEvents: 'none', // Não atrapalhar cliques
            filter: 'grayscale(100%)' // Opcional: deixar P&B para parecer marca d'água
          }}
        />
      )}

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3, color: '#20ac9c' }}>
          Cockpit
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Card
              sx={{
                backgroundColor: 'background.paper',
                height: 200,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#20ac9c' }}>
                  Nossa empresa
                </Typography>

                {loadingEmpresa ? (
                  <Preloader message="Carregando dados..." />
                ) : empresa ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {empresa.st_logotipo && (
                      <Box
                        component="img"
                        src={empresa.st_logotipo}
                        alt="Logo"
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid #eee'
                        }}
                      />
                    )}
                    <Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        {empresa.st_nome}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                        CNPJ/CPF: {empresa.st_cpfCnpj?.length > 11 ? formatarCNPJ(empresa.st_cpfCnpj) : formatarCPF(empresa.st_cpfCnpj)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                        Email: {empresa.st_email || 'Não informado'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                        Tel: {formatarTelefone(empresa.st_telefone) || 'Não informado'}
                      </Typography>
                      <Typography variant="body2" noWrap title={empresa.st_endereco} sx={{ fontSize: 13, color: 'text.secondary' }}>
                        End: {empresa.st_endereco || 'Não informado'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      Empresa não vinculada ou dados não encontrados.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card
              sx={{
                backgroundColor: 'background.paper',
                height: 200,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#20ac9c' }}>
                  Anotações corporativas
                </Typography>
                <Box sx={{ maxHeight: 120, overflowY: 'auto', pr: 1 }}>
                  {empresa && empresa.st_observacoes ? (
                    <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-line', color: 'text.secondary' }}>
                      {empresa.st_observacoes}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.secondary' }}>
                      Nenhuma anotação disponível.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card
              sx={{
                backgroundColor: 'background.paper',
                height: 200,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#20ac9c' }}>
                  Dados do Usuário
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    {user?.st_nome || user?.nome_completo || 'Usuário'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Email: {user?.st_email || user?.email || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Perfil: {(user?.en_perfil || user?.perfil || 'N/A').toUpperCase()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Status: {user?.bo_ativo !== false ? 'Ativo' : 'Inativo'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Renderizar Chat apenas se o usuário tiver permissão */}
        {(user?.modulos?.includes('Chat') || user?.modulos?.includes('chat') || user?.st_perfisPermitidos?.includes('Chat') || user?.perfil === 'superusuario') && (
          <Box sx={{ mt: 3 }}>
            <Card
              sx={{
                backgroundColor: 'background.paper',
                minHeight: 450, // Increased height for chat
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#20ac9c' }}>
                  Visão Geral - Chat
                </Typography>
                <ChatComponent />
              </CardContent>
            </Card>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', opacity: 0.8 }}>
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            <a
              href="https://site.saotech.com.br"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: '#20ac9c', fontWeight: 600, fontFamily: 'sans-serif' }}
            >
              SAOTech Softwares
            </a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
